/**
 * scanLocation — Sprint 2 (FE-9.2).
 *
 * Captures the GPS coordinates the user is at when they hit Save on a
 * scan, so the server can match `LOCATION_RADIUS` group rules and
 * suggest the right Crew (FE-9.1's auto-suggest).
 *
 * Design notes:
 *
 *  - **Best-effort, never blocking.** Returns `null` on any failure. Save
 *    must still work without GPS — Personal-only flow doesn't need it.
 *
 *  - **Permission cached per session.** If the user denies, we don't
 *    re-prompt for the rest of the session — that's just annoying.
 *    Stored in `sessionStorage` so a fresh tab gets a fresh prompt.
 *
 *  - **Coordinates rounded client-side to 4 decimals (~11m precision)
 *    BEFORE sending.** Matches the backend's PII reduction policy
 *    (BE-9.5). Never send centimeter precision the user didn't realize
 *    they were sharing.
 *
 *  - **EXIF fallback** is intentionally NOT implemented in this helper.
 *    The image data is available to `SegmentScreen`, not here. If the
 *    caller wants to chain EXIF, they call `captureScanLocation()` and
 *    if it returns null, they fall back to their own EXIF-extraction
 *    helper. Keeps this module testable in isolation.
 *
 *  - **No error toasts.** GPS is a silent enhancement. The `error`
 *    channel is just for the test suite + debug logs.
 */

const SESSION_KEY = '@founsi/scan_location_decision';

type CachedDecision = 'pending' | 'granted' | 'denied' | 'unsupported';

export interface ScanLocation {
  /** Latitude rounded to 4 decimals (~11m precision). */
  lat: number;
  /** Longitude rounded to 4 decimals (~11m precision). */
  lng: number;
}

/**
 * Round a number to 4 decimal places. Uses Math.round on the integer
 * representation rather than Number.toFixed (which returns a string and
 * is locale-fragile in some test envs).
 */
function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

function readDecision(): CachedDecision | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (raw === 'granted' || raw === 'denied' || raw === 'unsupported') {
      return raw;
    }
    return null;
  } catch {
    return null;
  }
}

function writeDecision(d: CachedDecision): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(SESSION_KEY, d);
  } catch {
    /* private mode / SSR — fall through silently */
  }
}

/**
 * Best-effort: ask the browser for the current position once, with a
 * sensible timeout. Returns the rounded coordinates, or null on any
 * failure (denied permission, timeout, no support, throws).
 *
 * Re-calling this within the same session is cheap when previously
 * denied — we short-circuit before invoking the geolocation API at all,
 * which avoids re-prompting browsers that don't honor the cached
 * decision.
 */
export async function captureScanLocation(
  options: { timeoutMs?: number } = {},
): Promise<ScanLocation | null> {
  const { timeoutMs = 8_000 } = options;

  // SSR / native runtime / no navigator → unsupported.
  if (
    typeof navigator === 'undefined' ||
    !navigator.geolocation ||
    typeof navigator.geolocation.getCurrentPosition !== 'function'
  ) {
    writeDecision('unsupported');
    return null;
  }

  // Short-circuit on a cached "denied" — don't re-prompt.
  const cached = readDecision();
  if (cached === 'denied' || cached === 'unsupported') {
    return null;
  }

  return new Promise<ScanLocation | null>((resolve) => {
    let settled = false;
    const settle = (value: ScanLocation | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    // Hard timeout in case the browser hangs without firing either
    // success/error callback (rare but observed on some Android WebViews).
    const timeoutId = setTimeout(() => {
      settle(null);
    }, timeoutMs);

    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timeoutId);
          writeDecision('granted');
          settle({
            lat: round4(pos.coords.latitude),
            lng: round4(pos.coords.longitude),
          });
        },
        (err) => {
          clearTimeout(timeoutId);
          // err.code === 1 (PERMISSION_DENIED) → cache so we don't
          // re-prompt this session. Other codes (POSITION_UNAVAILABLE,
          // TIMEOUT) leave the decision pending so a retry on the next
          // scan can succeed.
          if (err && err.code === 1) {
            writeDecision('denied');
          }
          settle(null);
        },
        {
          maximumAge: 60_000, // accept a 1-min-old cached fix
          timeout: timeoutMs,
          enableHighAccuracy: false, // we round to ~11m anyway
        },
      );
    } catch {
      clearTimeout(timeoutId);
      settle(null);
    }
  });
}

/* ── Test-only escape hatches ─────────────────────────────────────── */

/** Reset the session-cached decision. Tests use this between cases. */
export function __resetScanLocationDecisionForTests(): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    /* noop */
  }
}

/** Read the cached decision. Tests + debugging surfaces use this. */
export function getScanLocationDecisionForDebug(): CachedDecision | null {
  return readDecision();
}
